import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { MsalProvider } from '@azure/msal-react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { PublicClientApplication } from '@azure/msal-browser';
import { Link, useParams, useHistory } from 'react-router-dom';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';

import './index.css';
import { login } from '../../store/Auth';
import logo from '../../static/img/logo.png';
import { oauthConfig } from '../../store/config';
import { acceptInvite, getBranding } from '../../store/Settings';
import SocialLogin from '../../components/SocialLogin';
import OTPVerification from '../../components/OTPVerification';

// Initialize MSAL instance for Microsoft authentication
const msalConfig = {
  auth: {
    clientId: oauthConfig['microsoft']['clientId'],
    authority: oauthConfig['microsoft']['authority'],
    redirectUri: oauthConfig['microsoft']['redirectUri'],
  },
  cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false }
}

const msalInstance = new PublicClientApplication(msalConfig);
gsap.registerPlugin(SplitText, ScrollTrigger);
function Login() {
  const { token, hash } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const headingRefs = useRef([]);
  const paragraphRefs = useRef([]);
  const splitParagraphs = useRef([]);

  const {isLogin, auth, require2FA, twoFAData, requiresPasswordSetup} = useSelector(state => state['Auth']);
  const { currentBranding } = useSelector(state => state['Settings']);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showOTP, setShowOTP] = useState(false);
  const [showPassword, setShowPassword] = useState({ password: false });
  const togglePasswordVisibility = field => setShowPassword(prev => ({ ...prev, [field]: !prev[field] }))

  useEffect(() => { document.title = 'Login | ' + currentBranding?.['agencyName'] }, [currentBranding]);
  useEffect(() => {
    // Only redirect if authenticated and NOT requiring password setup
    if (auth && !requiresPasswordSetup && history['location']['pathname'].indexOf('/invite') < 0 && history['location']['pathname'].indexOf('/magicLogin') < 0) {
      history.push('/dashboard');
    }
  }, [auth, requiresPasswordSetup]);

  // Handle invite - runs only once on mount
  useEffect(() => {
    const inviteKey = `invite_${token}`;
    if (history['location']['pathname'].indexOf('/invite') >= 0 && token && !sessionStorage.getItem(inviteKey)) {
      sessionStorage.setItem(inviteKey, 'true');
      dispatch(acceptInvite({ token, history }));
    }
  }, []);

  // Handle magic login - runs only once using sessionStorage to prevent double dispatch
  useEffect(() => {
    const isMagicLoginPath = history['location']['pathname'].indexOf('/magicLogin') >= 0;
    const magicLoginKey = `magic_login_${hash}`;

    if (isMagicLoginPath && hash && !sessionStorage.getItem(magicLoginKey)) {
      sessionStorage.setItem(magicLoginKey, 'true');
      dispatch({ type: 'MAGIC_LOGIN_REQUEST', payload: { hash, history } });
    }
  }, []);
  
  // Handle 2FA requirement
  useEffect(() => {
    if (require2FA && twoFAData) {
      setShowOTP(true);
    }
  }, [require2FA, twoFAData]);

  const handleLogin = () => {
    dispatch(login({ data: { email, password }, history }));
  }
  
  const handleBackToLogin = () => {
    setShowOTP(false);
    dispatch({ type: 'CLEAR_2FA_STATE' });
    setPassword('');
  }

  useEffect(() => {
    // Reset any previous SplitText instances
    splitParagraphs.current.forEach((split) => split?.revert?.());
    splitParagraphs.current = [];

    // Animate each heading
    headingRefs.current.forEach((el, i) => {
      gsap.fromTo(
        el,
        { opacity: 0, filter: "blur(10px)", y: -20 },
        { opacity: 1, filter: "blur(0px)", y: 0, duration: 0.8, ease: "power3.out", delay: i * 0.3 }
      );
    });

    // Animate each paragraph with SplitText
    paragraphRefs.current.forEach((el, i) => {
      const split = new SplitText(el, { type: "chars" });
      splitParagraphs.current.push(split);

      gsap.fromTo(
        split.chars,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 1, stagger: 0.02, ease: "power2.out", delay: (headingRefs.current.length + i) * 0.1 }
      );
    });
  }, []);

  return (
    <div className='login-page'>
      <div className='logo-area'>
        { currentBranding?.['logo'] && <img className='login-page-logo' src={currentBranding?.['logo']} alt={currentBranding?.['agencyName']} /> }
      </div>
      <div className='auto-container'>
        <div className='row'>
          <div className='col-lg-6 col-md-6 col-sm-12 login-area'>
            <div className='left-img-area login-page-img'>
              <img src={require('../../static/img/login-page.png')} alt='' />
              <h2 ref={(el) => el && headingRefs.current.push(el)}>{currentBranding?.['agencyName']}</h2>
              <p ref={(el) => el && paragraphRefs.current.push(el)}>Access predictive AI insights, automated follow-ups<br />and always-on AI assistance.</p>
            </div>
          </div>
          <div className='col-lg-5 offset-lg-1 col-md-6 col-sm-12 login-area'>
            <div className='login-form'>
              {showOTP && twoFAData ? (
                <OTPVerification
                  history={history}
                  email={twoFAData.email}
                  userId={twoFAData.userId}
                  onBack={handleBackToLogin}
                />
              ) : (
                <>
              <div className='title-area'>
                <h2 ref={(el) => el && headingRefs.current.push(el)}>Welcome Back</h2>
                <p ref={(el) => el && paragraphRefs.current.push(el)}>Sign in to continue to your account</p>
              </div>

              {!currentBranding?.['isCustom'] && (
                <>
                  <GoogleOAuthProvider clientId={oauthConfig['google']['clientId']}>
                    <MsalProvider instance={msalInstance}>
                      <SocialLogin history={history} type='Login' />
                    </MsalProvider>
                  </GoogleOAuthProvider>

                  <div className='or-text'>
                    <p>OR</p>
                  </div>
                </>
              )}

              <ValidatorForm className='validator-form mt-2' onSubmit={handleLogin}>
                <div className='group-form'>
                  <label>Email</label>
                  <TextValidator
                    className='login-input-fields'
                    type='text'
                    name='email'
                    value={email}
                    margin='dense'
                    variant='outlined'
                    placeholder='Email here'
                    validators={['required', 'isEmail']}
                    onChange={(e) => setEmail(e['target']['value'])}
                    errorMessages={['Email can not be empty', 'Email is not valid']}
                  />
                </div>
                <div className='group-form password-group'>
                  <label>Password</label>
                  <TextValidator
                    margin='dense'
                    name='password'
                    value={password}
                    variant='outlined'
                    validators={['required']}
                    placeholder='Password here'
                    className='login-input-fields'
                    type={showPassword['password'] ? 'text' : 'password'}
                    errorMessages={['Password can not be empty']}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button type='button' className='toggle-password-visibility' onClick={() => togglePasswordVisibility('password')}>
                    <i className={`fa ${showPassword['password'] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                <div className='group-form text-center mt-4'>
                  <button type='submit' className='btn-style-new' disabled={isLogin}>
                    {!isLogin ? 'Sign In' : <div><i className='fa fa-spinner fa-spin'></i></div>}
                  </button>
                </div>
              </ValidatorForm>
              <div className='group-form checkbox-group'>
                <div className='group-form checkbox-groups'>
                  Don't have an account ? <Link className='forget-pass' to='/signup'> &nbsp; Sign up</Link>
                </div>
                <Link className='forget-pass' to='/forgot-password'>Forgot Password ?</Link>
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login;